import React, { ReactNode, useState } from 'react';
import { Divider, Checkbox } from 'antd';
import { X, Printer, Wallet } from '@phosphor-icons/react';
import { ActionButtonsProps, PurchaseOrderDetailComponentProps, PurchaseOrderStatusCode } from 'types';
import { formatDate } from 'utils';
import { QRCodeIcon, QRCodeImg } from 'utils/importFile';
import {
  IconCommon,
  BarcodeCommon,
  ButtonCommon,
  QRCodeModal,
  SelectProductModal,
  PaymentStatusNoti,
  UploadFileCommon,
} from 'components/common';
import { PaymentInfo, SectionHeader, StatusTag } from './DetailCommon';
import { isExpired } from 'utils/purchaseOrderUtils';
import { InventoryDocumentItem } from 'models';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
// Reusable Info Field Component

export const InfoField: React.FC<{ label: string; value: ReactNode; isHighlight?: boolean }> = ({
  label,
  value = '---',
  isHighlight = false,
}) => (
  <div>
    <div className='text-sm text-[var(--textBlack56)] mb-1'>{label}</div>
    <div className={`text-sm ${isHighlight ? 'font-bold text-[var(--primaryColor500)]' : 'font-medium'}`}>{value}</div>
  </div>
);

export const InfoGrid: React.FC<{ children: ReactNode; columns?: number; className?: string }> = ({
  children,
  columns = 4,
  className = '',
}) => <div className={`grid grid-cols-1 md:grid-cols-${columns} gap-4 px-6 py-3 ${className}`}>{children}</div>;

// Action Buttons Component

const ActionButtons: React.FC<ActionButtonsProps> = ({
  onPrint,
  onImport,
  onImportPartial,
  canBeImported,
  isImporting,
  isImported,
}) => (
  <div className='flex flex-col items-center justify-between gap-3 mt-6'>
    <ButtonCommon color='soft' className='w-[320px]' radius='rounded' onClick={onPrint}>
      <Printer size={20} className='mr-2' />
      In phiếu nhập
    </ButtonCommon>

    {canBeImported && (
      <>
        <ButtonCommon
          color='outline'
          radius='rounded'
          className='w-[320px] !bg-[var(--bgBlackOpacity10)]'
          onClick={onImportPartial}
        >
          Nhập hàng theo đợt
        </ButtonCommon>
        <ButtonCommon color='primary' radius='rounded' className='w-[320px]' onClick={onImport}>
          Bắt đầu nhập hàng
        </ButtonCommon>
      </>
    )}

    {isImporting && (
      <ButtonCommon color='primary' radius='rounded' className='w-[320px]' onClick={onImport}>
        Tiếp tục nhập hàng
      </ButtonCommon>
    )}
    {/* {isDelayed && (
      <ButtonCommon color='danger' radius='rounded' className='w-[320px]' isDisabled={true}>
        Phiếu nhập quá hạn
      </ButtonCommon>
    )} */}

    {isImported && (
      <ButtonCommon color='primary' radius='rounded' className='w-[320px]' isDisabled={true}>
        Đã nhập hàng
      </ButtonCommon>
    )}
  </div>
);

// Main Component
const PurchaseOrderDetailComponent: React.FC<PurchaseOrderDetailComponentProps> = ({
  paymentDisplay = true,
  staffDisplay = true,
  purchaseOrder,
  isModalView = true,
  // onClose,
  onImport,
  onPrint,
  onStartImporting,
  className = '',
  paymentStatusType,
  recivied = false,
  transactions,
  setFilesData = () => {},
  filesData,
  onNewFileUpload,
  onReturn,
}) => {
  // Helper to determine if actions should be shown based on status
  const canBeImported =
    (purchaseOrder.status?.code as string) === PurchaseOrderStatusCode.READY &&
    purchaseOrder?.inventoryDocumentItems?.length !== 0;
  const isImporting = (purchaseOrder.status?.code as string) === PurchaseOrderStatusCode.INPROGRESS;
  const isImported = (purchaseOrder.status?.code as string) === PurchaseOrderStatusCode.RECEIVED;
  const isDelayed = isExpired(
    purchaseOrder.status?.code as PurchaseOrderStatusCode,
    purchaseOrder.deliveryDate as Date,
  );
  const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);
  const [showSelectProductModal, setShowSelectProductModal] = useState(false);
  const isDesktop = useMediaQuery({ minWidth: 1280 });
  const [isPaymentPanelVisible, setIsPaymentPanelVisible] = useState(false);
  const navigate = useNavigate();
  const handleImportPartial = () => {
    setShowSelectProductModal(true);
  };
  const handleConfirmSelectProduct = (
    selectedItems: InventoryDocumentItem[],
    returnDate?: Date,
    updatedPrices?: Record<string, number>,
  ) => {
    navigate(`importing/${purchaseOrder.id}`, {
      state: {
        initialStep: 1,
        selectedItems: selectedItems,
        isPartial:
          selectedItems.length <
          (purchaseOrder?.inventoryDocumentItems?.length ? purchaseOrder.inventoryDocumentItems.length : 0),
        returnDate: returnDate,
        updatedPrices: updatedPrices,
      },
    });
    setShowSelectProductModal(false);
  };
  const togglePaymentPanel = () => {
    setIsPaymentPanelVisible((prev) => !prev);
  };
  const renderPaymentPanel = () => (
    <div className='bg-[var(--textBlack2)] p-4 rounded-[8px] border border-[var(--textBlack8)] shadow-sm flex flex-col justify-between items-center h-full'>
      <PaymentInfo purchaseOrder={purchaseOrder} recivied={recivied} transactions={transactions} />
      {isModalView! && (
        <ActionButtons
          onImportPartial={handleImportPartial}
          onPrint={onPrint}
          onStartImporting={onStartImporting}
          onImport={onImport}
          canBeImported={canBeImported}
          isImporting={isImporting}
          isImported={isImported}
          isDelayed={isDelayed}
        />
      )}
      {onReturn && (
        <ButtonCommon color='primary' radius='rounded' className='w-[320px]' onClick={onReturn}>
          Xác nhận trả hàng
        </ButtonCommon>
      )}
    </div>
  );

  return (
    <div className={`bg-white w-full ${isModalView ? 'rounded-[16px] p-6 ' : ''} ${className}`}>
      <QRCodeModal
        isOpen={isQRCodeModalOpen}
        onClose={() => setIsQRCodeModalOpen(false)}
        qrCodeUrl={QRCodeImg}
        companyName={purchaseOrder.supplier?.baseUser.name || 'CÔNG TY TNHH TÔN HOA SEN'}
        phoneNumber={purchaseOrder.supplier?.baseUser.phoneNumber}
      />
      <SelectProductModal
        isOpen={showSelectProductModal}
        onClose={() => setShowSelectProductModal(false)}
        orderItems={purchaseOrder.inventoryDocumentItems || []}
        onConfirm={handleConfirmSelectProduct}
        originalReturnDate={purchaseOrder.receivedDate ? purchaseOrder.receivedDate : purchaseOrder.deliveryDate}
      />
      <div className='flex flex-row gap-6'>
        <div className='flex-1'>
          {paymentStatusType && (
            <PaymentStatusNoti
              type='success'
              title='Xác nhận thanh toán thành công'
              subtitle={`Bạn vừa hoàn tất đặt nhập hàng từ nhà cung cấp ${purchaseOrder.supplier?.baseUser?.name || ''}, hệ thống đã lưu lại thông tin nhập hàng. Xin cảm ơn!`}
              highlightedText={purchaseOrder.supplier?.baseUser?.name || ''}
              bgColor='var(--sematicSuccessColor500)'
              className='mb-4'
            />
          )}
          {/* Order Creator Information */}
          {staffDisplay && (
            <div>
              <SectionHeader title='NGƯỜI TẠO ĐƠN' />
              <InfoGrid>
                <InfoField label='Mã phiếu nhập' isHighlight value={purchaseOrder.code} />
                <InfoField label='Mã đơn đặt' isHighlight value={purchaseOrder.inventoryDocument?.code} />
                <div className='md:col-span-2'>
                  <div className='text-sm text-[var(--textBlack56)] mb-1'>Trạng thái</div>
                  <StatusTag status={purchaseOrder.status?.code as PurchaseOrderStatusCode} isDelayed={isDelayed} />
                </div>
              </InfoGrid>

              <div className='mt-4 grid grid-cols-1 md:grid-cols-4 px-6 gap-4'>
                <InfoField label='Người tạo đơn' value={purchaseOrder.createdBy?.baseUser?.name} />
                <InfoField
                  label='Liên hệ'
                  value={purchaseOrder.createdBy?.baseUser?.phoneNumber || '---'}
                  isHighlight={true}
                />
                <InfoField label='Email' value={purchaseOrder.createdBy?.baseUser?.email} isHighlight={true} />
                <div>
                  <div className='text-sm text-[var(--textBlack56)] mb-1'>Thời gian tạo</div>
                  <div className='text-sm font-medium flex items-center'>
                    {formatDate(purchaseOrder.createdAt || '')}
                  </div>
                </div>
              </div>
            </div>
          )}
          {purchaseOrder.status?.code === PurchaseOrderStatusCode.RECEIVED && (
            <div className='flex flex-col mt-4'>
              <div className=' py-3'>
                <h2 className='text-xl font-bold'>QUẢN LÝ CHỨNG TỪ</h2>
              </div>
              <div className=''>
                <UploadFileCommon
                  filesData={filesData || []}
                  setFilesData={setFilesData}
                  onNewFileUpload={onNewFileUpload}
                  readOnly
                />
              </div>
            </div>
          )}
          {/* Supplier Information */}
          {staffDisplay && <Divider className='my-4 ' style={{ border: '1px solid white' }} />}
          <div>
            <SectionHeader title='THÔNG TIN CHI TIẾT NHÀ CUNG CẤP' />
            <InfoGrid columns={3}>
              <InfoField label='Mã nhập hàng' value={purchaseOrder.code} />
              <InfoField label='Nhà cung cấp' value={purchaseOrder.supplier?.baseUser.name} />
              <InfoField
                label='Barcode'
                value={
                  <BarcodeCommon
                    valueBarcode='123456789012'
                    size='sm'
                    onGenerate={() => {}}
                    isDisabled={true}
                    displayValue={false}
                    displayGenerate={false}
                    className='!justify-start'
                  />
                }
              />
            </InfoGrid>

            <InfoGrid columns={3} className='mt-4'>
              <InfoField label='Liên hệ' value={purchaseOrder.supplier?.baseUser?.phoneNumber} isHighlight={true} />
              <InfoField label='Email' value={purchaseOrder.supplier?.baseUser?.email} isHighlight={true} />
              <div>
                <div className='text-sm text-[var(--textBlack56)] mb-1'>Zalo QR</div>
                <div className='text-sm flex items-center justify-start'>
                  <IconCommon src={QRCodeIcon} className='mr-2' />
                  <span
                    onClick={() => setIsQRCodeModalOpen(true)}
                    className='text-[var(--primaryColor500)] font-medium cursor-pointer'
                  >
                    Nhấp vào để xem
                  </span>
                </div>
              </div>
            </InfoGrid>

            <div className='mt-4 px-6'>
              <div className='text-sm text-[var(--textBlack56)]'>Mô tả đơn đặt hàng</div>
              <div className='text-sm mt-1'>{purchaseOrder.description || '---'}</div>
            </div>
          </div>

          {/* Recipient Information */}
          <Divider className='my-4 ' style={{ border: '1px solid white' }} />
          <div className='mb-6'>
            <SectionHeader title='NGƯỜI NHẬN' />
            <InfoGrid columns={3}>
              <InfoField label='Tên người nhận' value={purchaseOrder.userName} />
              <InfoField label='Số điện thoại' value={purchaseOrder.userPhoneNumber} />
              <InfoField label='Email' value={purchaseOrder.userEmail} isHighlight={true} />
            </InfoGrid>

            <div className='mt-4 flex items-center px-6'>
              <Checkbox checked className='mr-2 checkbox-disabled cursor-not-allowed' />
              <span className='text-sm'>Thông báo đến nhân viên để họ biết (Zalo, số điện thoại, Email,...)</span>
            </div>
            <div className='mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 px-6'>
              <InfoField label='Kho nhập hàng' value={purchaseOrder.warehouse?.name} />
              <InfoField
                label='Thời gian nhập hàng'
                value={formatDate(purchaseOrder.receivedDate ? purchaseOrder.receivedDate : purchaseOrder.deliveryDate)}
              />
            </div>

            <div className='mt-4 px-6'>
              <div className='text-sm text-[var(--textBlack56)]'>Ghi chú</div>
              <div className='text-sm mt-1'>{purchaseOrder.note || '---'}</div>
            </div>
          </div>
        </div>

        {/* Right side - Payment information  */}
        {paymentDisplay && isDesktop && <div className='w-[444px] min-w-[444px]'>{renderPaymentPanel()}</div>}
      </div>
      {paymentDisplay && !isDesktop && (
        <div className='fixed bottom-6 right-6 z-40 cursor-pointer' onClick={togglePaymentPanel}>
          <div className='bg-[var(--primaryColor500)] text-white rounded-full max-w-fit px-4 h-16 flex items-center justify-center shadow-lg'>
            <Wallet size={28} weight='fill' /> <div className='text-lg font-bold ml-2'>Thông tin thanh toán</div>
          </div>
        </div>
      )}

      {/* Mobile Payment Panel Overlay */}
      {paymentDisplay && !isDesktop && (
        <div className={`fixed inset-0 z-50 flex justify-end ${isPaymentPanelVisible ? 'visible' : 'invisible'}`}>
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${
              isPaymentPanelVisible ? 'opacity-50' : 'opacity-0'
            }`}
            onClick={() => setIsPaymentPanelVisible(false)}
          ></div>

          <div
            className={`relative bg-white min-w-[500px] flex flex-col max-w-[550px] h-full transform transition-transform duration-300 ease-out shadow-xl overflow-auto ${
              isPaymentPanelVisible ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className='sticky top-0 bg-white p-4 border-b border-gray-200 flex items-center justify-between z-10'>
              <div className='text-lg font-bold'></div>
              <button
                className='text-gray-500 hover:text-gray-700 focus:outline-none'
                onClick={() => setIsPaymentPanelVisible(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div className='p-4 flex-1'>{renderPaymentPanel()}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderDetailComponent;
